import { useState } from 'react';
import { Banner } from '../../components/Banner';
import { Button } from '../../components/Button';
import { Input } from '../../components/Input';
import { Modal } from '../../components/Modal';
import { newIdempotencyKey } from '../../lib/api';
import { describeApiError } from '../qrs/errors';
import { itemsOf, useApiQuery } from '../qrs/hooks';
import { exclusionText, memberLabel } from '../qrs/states';
import type { CustodyResult, Lot, Member } from '../qrs/types';
import { useOrgApi } from '../qrs/useOrgApi';
import { parseCodes } from './lotUtils';


function custodySummary(r: CustodyResult): string {
  const parts = [`${(r.assigned ?? 0).toLocaleString('es-CL')} asignados`];
  if (r.transferred) parts.push(`${r.transferred.toLocaleString('es-CL')} cambiaron de custodio`);
  if (r.excluded?.length) parts.push(`${r.excluded.length} excluidos`);
  const text = `${parts.join(' · ')}.`;
  return r.shortfall ? `${text} Faltaron ${r.shortfall.toLocaleString('es-CL')} impresos sin asignar para completar la cantidad.` : text;
}

export function AssignCustodyModal({ lot, open, onClose, onDone }: { lot: Lot; open: boolean; onClose: () => void; onDone: () => void }) {
  const client = useOrgApi();
  const members = useApiQuery((signal) => client.get<{ items: Member[] }>('/v1/members', { signal }).then(itemsOf), [client]);
  const custodians = (members.data ?? []).filter((m) => m.role === 'seller' || m.role === 'admin' || m.role === 'owner');
  const unassigned = lot.counts?.impreso ?? 0;
  const [mode, setMode] = useState<'quantity' | 'codes'>('quantity');
  const [quantity, setQuantity] = useState(String(Math.min(100, unassigned || 100)));
  const [codes, setCodes] = useState('');
  const [custodianUid, setCustodianUid] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CustodyResult | null>(null);

  const qty = Number(quantity);
  const codeList = parseCodes(codes);
  const valid = Boolean(custodianUid) && (mode === 'quantity' ? Number.isInteger(qty) && qty >= 1 && qty <= 5000 : codeList.length > 0);

  async function submit() {
    setError(null);
    setSaving(true);
    try {
      const body = mode === 'quantity' ? { custodianUid, quantity: qty } : { custodianUid, codes: codeList };
      const res = await client.post<CustodyResult>(`/v1/lots/${encodeURIComponent(lot.id)}/custody`, body, { idempotencyKey: newIdempotencyKey() });
      setResult(res);
      onDone();
    } catch (err) {
      setError(describeApiError(err, 'No pudimos asignar la custodia.'));
    } finally {
      setSaving(false);
    }
  }

  const excluded = result?.excluded ?? [];

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Asignar custodia"
      footer={
        result ? (
          <Button onClick={onClose}>Listo</Button>
        ) : (
          <>
            <Button variant="secondary" onClick={onClose}>
              Cancelar
            </Button>
            <Button onClick={() => void submit()} loading={saving} disabled={!valid}>
              Asignar
            </Button>
          </>
        )
      }
    >
      <p className="text-sm text-ink-muted">
        Lote <span className="font-mono">{lot.id}</span> · {unassigned.toLocaleString('es-CL')} impresos sin asignar. Solo los QR impresos pasan a custodia; si ya están en custodia, cambian de custodio.
      </p>
      {result ? (
        <div aria-live="polite" className="space-y-2">
          <Banner tone="success">{custodySummary(result)}</Banner>
          {excluded.length > 0 && (
            <ul className="max-h-40 overflow-y-auto rounded-md border border-line bg-gray-50 p-2 font-mono text-xs text-gray-700">
              {excluded.map((e, i) => (
                <li key={`${e.code ?? e.id}-${i}`}>{exclusionText(e)}</li>
              ))}
            </ul>
          )}
        </div>
      ) : (
        <>
          <fieldset className="flex gap-4 text-sm">
            <legend className="sr-only">Qué asignar</legend>
            <label className="flex items-center gap-1.5">
              <input type="radio" name="custody-mode" checked={mode === 'quantity'} onChange={() => setMode('quantity')} /> Cantidad
            </label>
            <label className="flex items-center gap-1.5">
              <input type="radio" name="custody-mode" checked={mode === 'codes'} onChange={() => setMode('codes')} /> Selección de códigos
            </label>
          </fieldset>
          {mode === 'quantity' ? (
            <Input label="Cantidad" inputMode="numeric" value={quantity} onChange={(e) => setQuantity(e.target.value)} />
          ) : (
            <div className="flex flex-col gap-1">
              <label htmlFor="custody-codes" className="text-xs font-medium text-ink-muted">
                Códigos (separados por coma o espacio)
              </label>
              <textarea id="custody-codes" value={codes} onChange={(e) => setCodes(e.target.value)} className="h-20 w-full rounded-sm border border-line px-3 py-2 font-mono text-sm uppercase" placeholder="K7MX, P2LQ" />
              <span className="text-xs text-ink-muted">{codeList.length} códigos</span>
            </div>
          )}
          <div className="flex flex-col gap-1">
            <label htmlFor="custody-member" className="text-xs font-medium text-ink-muted">
              Custodio
            </label>
            <select id="custody-member" value={custodianUid} onChange={(e) => setCustodianUid(e.target.value)} className="w-full rounded-sm border border-line bg-white px-3 py-2 text-sm">
              <option value="">{members.loading ? 'Cargando miembros…' : 'Elige un miembro'}</option>
              {custodians.map((m) => (
                <option key={m.uid} value={m.uid}>
                  {memberLabel(m)}
                </option>
              ))}
            </select>
            {members.error ? <span className="text-xs text-error-text">{describeApiError(members.error, 'No pudimos cargar los miembros.')}</span> : null}
          </div>
          {error && <Banner tone="error">{error}</Banner>}
        </>
      )}
    </Modal>
  );
}
